<template>
  <div class="page-content">
    <!-- Loading State -->
    <v-row v-if="loading" style="margin-top: 16px;">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p>Loading payment data...</p>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <template v-else>
      <!-- Tabs -->
      <v-card class="mx-auto elevation-2 tabs-card" style="margin-top: 16px;">
        <v-container fluid class="pa-2">
          <v-row align="center" justify="space-between" no-gutters>
            <v-col cols="auto">
              <v-tabs 
                v-model="activeTab" 
                class="dashboard-tab" 
                show-arrows 
                next-icon="mdi-arrow-right-bold-box-outline" 
                prev-icon="mdi-arrow-left-bold-box-outline"
                style="background-color: white; color: #2C74B3;"
              >
                <v-tab v-if="!isSatelliteMonitor" value="studio">Studio</v-tab>
                <v-tab v-if="!isSatelliteMonitor" value="morning">Morning</v-tab>
                <v-tab v-if="!isSatelliteMonitor" value="evening">Evening</v-tab>
                <v-tab v-if="!isSatelliteMonitor" value="night">Night</v-tab>
                <v-tab value="satellite">Satellite</v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <!-- Sexy Summary Card -->
      <v-row style="margin-top: 16px;">
        <v-col cols="12">
          <v-card 
            class="elevation-4 sexy-summary-card" 
            :style="{ height: isCollapsed ? '48px' : '200px', transition: 'height 0.3s ease' }"
            style="border-radius: 12px; overflow: hidden;"
          >
            <div 
              style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; height: 48px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white;"
            >
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="font-size: 18px; font-weight: 700; letter-spacing: 1px;">Payments Dashboard</span>
                <v-icon 
                  @click="toggleCollapse" 
                  style="cursor: pointer; color: white;" 
                  size="24"
                >
                  {{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                </v-icon>
              </div>
              <div style="display: flex; align-items: center; gap: 12px;">
                <v-btn
                  :class="['btn-secondary']"
                  style="padding: 0 14px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white;"
                  @click="viewPeriod"
                  :disabled="!selectedPeriod"
                >
                  <v-icon size="18" color="white" style="margin-right: 5px;">mdi-refresh</v-icon>
                  Recalc
                </v-btn>
                <span style="font-size: 14px; font-style: italic; opacity: 0.8;">Edit Commission</span>
                <v-btn 
                  :class="['btn-secondary']" 
                  style="padding: 0 16px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white; margin-right: 16px" 
                  @click="toggleEditCommissions"
                  :disabled="!canEditPayments || !hasAnyUnlockedModels"
                >
                  <v-icon size="20" color="white">mdi-percent</v-icon>
                </v-btn>
                <span style="font-size: 14px; font-style: italic; opacity: 0.8;">Download</span>
                <span 
                  style="cursor: pointer; font-size: 14px; font-weight: 600; color: white; opacity: 0.9;" 
                  @click="toggleDownloadFormat"
                >
                  {{ downloadFormat.toUpperCase() }}
                </span>
                <v-btn 
                  :class="['btn-secondary', downloadFormat === 'pdf' ? 'active-button' : '']" 
                  style="padding: 0 16px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white;" 
                  @click="downloadData"
                >
                  <v-icon size="20">mdi-download</v-icon>
                </v-btn>
              </div>
            </div>

            <v-expand-transition>
              <div 
                v-show="!isCollapsed" 
                style="padding: 12px 16px 16px 16px; display: flex; justify-content: space-between; background-color: white; color: black;"
              >
                <div style="flex: 1; display: flex; flex-direction: column; gap: 8px;">
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Total Model Earnings (COP)</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(Math.floor(totalGrossEarnings), 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Total Model Adjustments (COP)</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(totalAdjustments, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Total Model Deductions (COP)</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(totalDeductions, 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; padding-top: 4px;">
                    <span style="font-size: 16px; font-weight: 700; text-transform: uppercase;">Net Payment</span>
                    <span style="font-size: 16px; font-weight: 700;">{{ formatCurrency(Math.floor(netPaymentTotal), 'COP', true) }}</span>
                  </div>
                </div>
                <div style="flex: 1; display: flex; flex-direction: column; gap: 8px; margin-left: 24px;">
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Morning Monitor Commission (COP)</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(Math.floor(morningCommission), 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Evening Monitor Commission (COP)</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(Math.floor(eveningCommission), 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px;">
                    <span style="font-size: 14px; font-weight: 600;">Night Monitor Commission (COP)</span>
                    <span style="font-size: 14px; font-weight: 400;">{{ formatCurrency(Math.floor(nightCommission), 'COP', true) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; padding-top: 4px;">
                    <span style="font-size: 16px; font-weight: 700; text-transform: uppercase;">Total Commission</span>
                    <span style="font-size: 16px; font-weight: 700;">{{ formatCurrency(Math.floor(totalCommission), 'COP', true) }}</span>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </v-card>
        </v-col>
      </v-row>

      <!-- Cards -->
      <v-row style="margin-top: 10px;">
        <!-- View Model Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'viewModel' ? 'active-card' : '']" @click="setActiveCard('viewModel')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-account-search</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">View Model</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; padding-bottom: 40px;">
              <v-select
                v-model="modelFilter"
                :items="filteredModels.map(model => `${model.fullName} (${model.stageName})`)"
                label="Select Model"
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                style="font-size: 14px;"
              ></v-select>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
              <v-btn :class="['btn-secondary', activeCard === 'viewModel' ? 'active-button' : '']" style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" @click="viewModel">View</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Main Sites TRM Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'mainTrm' ? 'active-card' : '']" @click="setActiveCard('mainTrm')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-currency-usd</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Main Sites TRM</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; padding-bottom: 40px;">
              <v-text-field v-model.number="usdXe" label="Xe Rate" type="number" outlined hide-details prepend-inner-icon="mdi-swap-horizontal" style="font-size: 14px;" :disabled="((!isSatelliteMonitor && isPeriodLocked) || !canEditPayments)"></v-text-field>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
            <v-btn :class="['btn-secondary', activeCard === 'mainTrm' ? 'active-button' : '']" style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" @click="setMainTrm" :disabled="!usdXe || ((!isSatelliteMonitor && isPeriodLocked) || !canEditPayments)">Set</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Chaturbate TRM Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'cbTrm' ? 'active-card' : '']" @click="setActiveCard('cbTrm')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-webcam</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Chaturbate TRM</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px; padding-bottom: 40px;">
              <v-text-field v-model.number="cbXe" label="Xe Rate" type="number" outlined hide-details prepend-inner-icon="mdi-swap-horizontal" style="font-size: 14px;" :disabled="((!isSatelliteMonitor && isPeriodLocked) || !canEditPayments)"></v-text-field>
            </v-card-text>
            <v-card-actions style="justify-content: center; margin-top: -16px; margin-bottom: 12px; position: absolute; bottom: 0; width: 100%;">
            <v-btn :class="['btn-secondary', activeCard === 'cbTrm' ? 'active-button' : '']" style="padding: 0 40px; height: 36px; font-size: 14px; background-color: #1976d2; color: white;" @click="setCbTrm" :disabled="!cbXe || ((!isSatelliteMonitor && isPeriodLocked) || !canEditPayments)">Set</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Period Card -->
        <v-col cols="12" md="3">
          <v-card :class="['elevation-2', activeCard === 'period' ? 'active-card' : '']" @click="setActiveCard('period')" style="min-height: 180px; border-radius: 6px;">
            <v-card-title style="position: relative; display: flex; align-items: center; padding: 8px 16px;">
              <v-icon style="margin-right: 8px; font-size: 28px;">mdi-calendar-multiple-check</v-icon>
              <span style="position: absolute; left: 50%; transform: translateX(-50%); font-size: 16px; font-weight: 600;">Period</span>
            </v-card-title>
            <v-card-text style="padding: 4px 16px;">
              <v-select 
                v-model="selectedPeriod" 
                :items="periodOptions" 
                item-title="text" 
                item-value="value" 
                outlined 
                style="font-size: 14px;" 
                @update:modelValue="value => { selectedPeriod = value; fetchModels(); }"
              ></v-select>
            </v-card-text>
            <v-card-actions class="payments-period-actions">
              <v-btn
                icon
                class="payments-period-arrow"
                :disabled="!canGoToPreviousPeriod"
                aria-label="Previous period"
                @click.stop="goToPreviousPeriod"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                icon
                class="payments-period-arrow"
                :disabled="!canGoToNextPeriod"
                aria-label="Next period"
                @click.stop="goToNextPeriod"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn 
                icon 
                rounded="full" 
                style="height: 36px; width: 36px;"
                @click="toggleLock" 
                :disabled="!selectedPeriod || !isAdmin"
              >
                <v-icon :color="isPeriodLocked ? 'black' : '#1976d2'">
                  {{ isPeriodLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Chips Below Cards -->
      <v-row style="margin-top: 16px;" v-if="selectedModels.length > 0">
        <v-col cols="12">
          <div style="display: flex; flex-wrap: wrap; gap: 8px;">
            <v-chip
              v-for="modelName in selectedModels"
              :key="modelName"
              closable
              @click:close="removeModelChip(modelName)"
              color="#1976d2"
              text-color="white"
              style="font-size: 14px;"
            >
              {{ modelName }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- Divider Bar -->
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; height: 48px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white; border-radius: 6px; margin-top: 28px;">
        <h2 style="font-size: 18px; font-weight: 600; color: white;">Model Payments</h2>
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              style="text-transform: none; font-size: 1rem; background-color: transparent; color: white;"
            >
              <span style="font-weight: 400; margin-right: 4px;">Sort by:</span>
              {{ sortOption }}
              <v-icon small style="color: white;">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              density="compact"
              v-for="option in sortOptions"
              :key="option"
              @click="sortModels(option)"
            >
              <v-list-item-title>{{ option }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Model Cards -->
      <div v-if="models.length === 0">
        No payment data found for your studio and selected period.
      </div>
      <template v-for="model in filteredModels" :key="model.id">
        <v-card class="elevation-2" style="padding: 2px 16px; margin-top: 20px; border-radius: 6px;">
          <div :style="getModelHeaderStyle(model)">
            <div style="display: grid; grid-template-columns: 13% 26% 12% 12% 12% 12% 12%; align-items: center; min-height: 40px; width: 100%;">

              <!-- Model Name spans Platform + Period columns (because names are long) -->
              <div style="grid-column: 1 / span 2; min-width: 0;">
                <h2 style="font-size: 16px; font-weight: 700; margin: 0; line-height: 1.15; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  {{ model.fullName }}
                </h2>
              </div>

              <!-- Stage Name aligns with Currency column -->
              <div style="grid-column: 3; min-width: 0; display: flex; align-items: center; padding-top: 2px;">
                <span style="display: inline-block; padding: 3px 10px; border-radius: 999px; background: rgba(255,255,255,0.35); border: 1px solid rgba(66,81,102,0.12); color: #425166; font-size: 13px; font-weight: 600; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  {{ model.stageName }}
                </span>
              </div>

              <!-- Period aligns with Commission column -->
              <div style="grid-column: 5; min-width: 0; padding-left: 8px;">
                <p style="font-size: 14px; font-weight: 600; margin: 0; color: #425166; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  {{ model.period }}
                </p>
              </div>

              <!-- Logo aligns with Total COP column -->
              <div style="grid-column: 7; display: flex; justify-content: flex-end; align-items: center;">
                <v-img src="/logo_mgroup.png" style="width: 110px; height: 40px; object-fit: contain;"></v-img>
              </div>

            </div>
          </div>

          <v-row no-gutters style="height: 1px; margin: 6px 0 6px 0;">
            <v-col cols="12" style="padding: 0;">
              <div style="height: 1px; background-color: #cfd7e3;"></div>
            </v-col>
          </v-row>

          <template v-for="(payCard, payCardIndex) in getModelPaymentCards(model)" :key="payCard.card_key">
            <div style="border: 1px solid #e8e8e8; border-radius: 6px; margin-top: 6px; margin-bottom: 10px; overflow: hidden; background: white;">
              <div style="display: flex; align-items: center; min-height: 28px; padding: 0 8px; box-sizing: border-box; background: #f8f8f8; border-bottom: 1px solid #e0e0e0;">
                <strong style="width: 13%; font-size: 16px; font-weight: 700; line-height: 28px;">{{ payCard.earningsTitle }}</strong>
                <span style="width: 26%; font-size: 13px; color: #666; line-height: 28px;">
                  &nbsp;
                </span>
              </div>

            <!-- Gross Earnings Table -->
            <v-table density="compact" style="width: 100%; table-layout: fixed;">
              <thead>
                <tr>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 13%;">Platform</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 26%;">Period</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Earnings</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Currency</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Commission</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">TRM</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">&nbspTotal COP</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="earn in payCard.earnings" :key="`${payCard.card_key}-${earn.platform}`">
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 13%;">{{ earn.platform }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 26%;">{{ earn.period }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ formatCurrency(earn.gross_earnings || earn.earnings, earn.currency) }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ earn.platform === 'XLove' ? 'EUR' : earn.currency }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">
                    <span v-if="!isEditingCommissions">{{ displayCommission(earn.commission) }}</span>
                    <v-text-field
                      v-else
                      v-model.number="earn.commission"
                      type="number"
                      min="0"
                      max="100"
                      dense
                      hide-details
                      style="width: 120px;"
                      @input="updateCopTotal(model, earn)"
                      :disabled="!canEditPayments || model.isLocked"
                    ></v-text-field>
                  </td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">
                    {{ getEarningTrm(earn) }}
                  </td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;"><span style="visibility: hidden;">-</span>$&nbsp;{{ earn.copTotal !== null ? formatCurrency(displayCopForPositiveTotal(earn.copTotal), 'COP', true) : 'N/A' }}</td>
                </tr>
              </tbody>
            </v-table>

            <!-- Combined Adjustments and Deductions Table -->
            <v-table
              v-if="hasAdjustmentsInCard(payCard) || hasDeductionsInCard(payCard) || true"
              density="compact"
              style="width: 100%; table-layout: fixed; margin-top: 5px;"
            >
              <thead v-if="hasAdjustmentsInCard(payCard)">
                <tr>
                  <th colspan="7" style="font-size: 14px; font-weight: 600; padding: 6px 8px; text-align: left;">Adjustments</th>
                </tr>
                <tr>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 13%;">Type</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 26%;">Description</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Amount</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Xe</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Commission</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;"># / Quotas</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">&nbspTotal COP</th>
                </tr>
              </thead>
              <tbody v-if="hasAdjustmentsInCard(payCard)">
                <tr v-for="adj in payCard.adjustments" :key="'adj-' + payCard.card_key + '-' + adj.id">
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 13%;">{{ adj.type }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 26%;">{{ adj.description }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ formatCurrency(adj.amount, adj.currency, true) }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ adj.currency }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">
                    <span v-if="!isEditingCommissions || !canEditAdjustmentCommission(adj)">{{ displayAdjustmentCommission(adj, model) }}</span>
                    <v-text-field
                      v-else
                      :model-value="getAdjustmentCommissionPercent(adj, model)"
                      @update:modelValue="value => updateAdjustmentCommissionValue(model, adj, value)"
                      type="number"
                      min="0"
                      max="100"
                      dense
                      hide-details
                      style="width: 90px;"
                      :disabled="!canEditPayments || model.isLocked"
                    ></v-text-field>
                  </td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ adj.number }} / {{ adj.quotas }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;"><span style="visibility: hidden;">-</span>$&nbsp;{{ adj.copTotal !== null ? formatCurrency(Math.floor(adj.copTotal * adj.sign), 'COP', true) : 'N/A' }}</td>
                </tr>
              </tbody>
              <thead v-if="hasDeductionsInCard(payCard)">
                <tr>
                  <th colspan="7" style="font-size: 14px; font-weight: 700; text-align: left; padding: 6px 8px;">Deductions</th>
                </tr>
                <tr>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 13%;">Type</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 26%;">Description</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Amount</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Xe</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">Commission</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;"># / Quotas</th>
                  <th style="font-size: 12px; font-weight: 600; color: #000000; text-align: left; padding: 4px 8px; width: 12%;">&nbspTotal COP</th>
                </tr>
              </thead>
              <tbody v-if="hasDeductionsInCard(payCard)">
                <tr v-for="ded in payCard.deductions" :key="'ded-' + payCard.card_key + '-' + ded.id">
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 13%;">{{ ded.type }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 26%;">{{ ded.description }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ formatCurrency(ded.amount, ded.currency, true) }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ ded.currency }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ displayAdjustmentCommission(ded, model) }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ ded.number }} / {{ ded.quotas }}</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">{{ ded.copTotal !== null ? '- $ ' + formatCurrency(Math.floor(ded.copTotal), 'COP', true) : 'N/A' }}</td>
                </tr>
                <!-- Retention 4% deduction row -->
                <tr
                  v-if="payCard.retentionAmount > 0"
                  :key="'ded-retention-4pct-' + payCard.card_key"
                  style="background-color: #fbeee6;"
                >
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 13%;">Discount</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 26%;">Retención de la Fuente</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">4%</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">COP</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">100%</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">1 / 1</td>
                  <td style="font-size: 14px; text-align: left; padding: 4px 8px; width: 12%;">
                    - $ {{ formatCurrency(Math.floor(payCard.retentionAmount), 'COP', true) }}
                  </td>
                </tr>
              </tbody>
              <tbody>
                <tr style="line-height: 0;">
                  <td colspan="7" style="border-top: 1px solid #e0e0e0; padding: 0; height: 1px;"></td>
                </tr>
                <tr style="background-color: #fafafa;">
                  <td :style="(payCard.applied_pending_amount !== 0 || payCard.pending_amount !== 0 || payCard.minimum_payment_applied) ? 'font-size: 14px; font-weight: 600; padding: 10px 8px; width: 13%; border-bottom: none !important;' : 'font-size: 14px; font-weight: 600; padding: 10px 8px; width: 13%;'">
                    Paid on
                  </td>
                  <td :style="(payCard.applied_pending_amount !== 0 || payCard.pending_amount !== 0 || payCard.minimum_payment_applied) ? 'font-size: 14px; font-weight: 600; padding: 10px 8px; width: 26%; border-bottom: none !important;' : 'font-size: 14px; font-weight: 600; padding: 10px 8px; width: 26%;'">
                    Date: {{ payCard.paymentDate }}
                  </td>
                  <template v-if="payCard.applied_pending_amount !== 0">
                    <td colspan="2" style="padding: 10px 8px; border-bottom: none !important;"></td>
                    <td colspan="2" style="text-align: right; font-weight: 500; font-size: 14px; color: #616161; padding: 10px 48px 10px 8px; white-space: nowrap; border-bottom: none !important;">
                      Previous Pending Amount:
                    </td>
                    <td style="font-weight: 600; font-size: 14px; color: #616161; padding: 10px 8px; text-align: left; width: 12%; border-bottom: none !important;">
                      <template v-if="payCard.applied_pending_amount < 0">
                        -$&nbsp;{{ formatCurrency(Math.abs(displayCopForSummary(payCard.applied_pending_amount)), 'COP', true) }}
                      </template>
                      <template v-else>
                        <span style="visibility: hidden;">-</span>$&nbsp;{{ formatCurrency(displayCopForSummary(payCard.applied_pending_amount), 'COP', true) }}
                      </template>
                    </td>
                  </template>
                  <template v-else>
                    <td colspan="2" :style="(payCard.pending_amount !== 0 || payCard.minimum_payment_applied) ? 'padding: 10px 8px; border-bottom: none !important;' : 'padding: 10px 8px;'"></td>
                    <td colspan="2" :style="(payCard.pending_amount !== 0 || payCard.minimum_payment_applied) ? 'text-align: right; font-weight: 600; font-size: 14px; padding: 10px 48px 10px 8px; white-space: nowrap; border-bottom: none !important;' : 'text-align: right; font-weight: 600; font-size: 14px; padding: 10px 48px 10px 8px; white-space: nowrap;'">
                      Net Payment:
                    </td>
                    <td :style="(payCard.pending_amount !== 0 || payCard.minimum_payment_applied) ? 'font-weight: 700; font-size: 14px; padding: 10px 8px; text-align: left; width: 12%; border-bottom: none !important;' : 'font-weight: 700; font-size: 14px; padding: 10px 8px; text-align: left; width: 12%;'">
                      <span style="visibility: hidden;">-</span>$&nbsp;{{ payCard.net_payment !== null ? formatCurrency(displayCopForPositiveTotal(payCard.net_payment), 'COP', true) : 'N/A' }}
                    </td>
                  </template>
                </tr>
                <tr v-if="payCard.applied_pending_amount !== 0" style="background-color: #fafafa;">
                  <td colspan="4" style="padding: 0 8px 0 8px; border-bottom: none !important;"></td>
                  <td colspan="2" style="text-align: right; font-weight: 600; font-size: 14px; padding: 0 48px 0 8px; white-space: nowrap; border-bottom: none !important;">
                    Net Payment:
                  </td>
                  <td style="font-weight: 700; font-size: 14px; padding: 0 8px 0 8px; text-align: left; width: 12%; border-bottom: none !important;">
                    <span style="visibility: hidden;">-</span>$&nbsp;{{ payCard.net_payment !== null ? formatCurrency(displayCopForPositiveTotal(payCard.net_payment), 'COP', true) : 'N/A' }}
                  </td>
                </tr>
                <tr v-if="payCard.minimum_payment_applied" style="background-color: #fafafa;">
                  <td style="padding: 10px 8px; font-size: 14px; font-weight: 600; color: #616161; border-bottom: none !important; width: 13%;">
                    Payment
                  </td>
                  <td style="padding: 10px 8px; font-size: 14px; font-weight: 500; color: #616161; white-space: nowrap; border-bottom: none !important; width: 26%;">
                    Minimum Required: $ {{ formatCurrency(Math.floor(payCard.minimum_payment_required || 60000), 'COP', true) }} COP
                  </td>
                  <td colspan="2" style="padding: 10px 8px; border-bottom: none !important;"></td>
                  <td colspan="2" style="text-align: right; font-weight: 500; font-size: 14px; color: #616161; padding: 10px 48px 10px 8px; white-space: nowrap; border-bottom: none !important;">
                    Pending Amount:
                  </td>
                  <td style="font-weight: 600; font-size: 14px; color: #616161; padding: 10px 8px; text-align: left; width: 12%; border-bottom: none !important;">
                    <span style="visibility: hidden;">-</span>$&nbsp;{{ formatCurrency(displayCopForSummary(payCard.pending_amount || 0), 'COP', true) }}
                  </td>
                </tr>
                <tr v-if="payCard.pending_amount !== 0 && !payCard.minimum_payment_applied" style="background-color: #fafafa;">
                  <td colspan="4" style="padding: 10px 8px; vertical-align: middle;"></td>
                  <td colspan="2" style="text-align: right; font-weight: 500; font-size: 14px; color: #616161; padding: 10px 48px 10px 8px; white-space: nowrap; vertical-align: middle;">
                    {{ payCard.applied_pending_amount !== 0 ? 'New Pending Amount:' : 'Pending Amount:' }}
                  </td>
                  <td style="font-weight: 600; font-size: 14px; color: #616161; padding: 10px 8px; text-align: left; width: 12%; vertical-align: middle;">
                    <template v-if="payCard.pending_amount < 0">
                      -$&nbsp;{{ formatCurrency(Math.abs(displayCopForSummary(payCard.pending_amount)), 'COP', true) }}
                    </template>
                    <template v-else>
                      <span style="visibility: hidden;">-</span>$&nbsp;{{ formatCurrency(displayCopForSummary(payCard.pending_amount), 'COP', true) }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-table>

            
            </div>

            <v-row v-if="payCardIndex < getModelPaymentCards(model).length - 1" no-gutters style="margin: 12px 0;">
              <v-col cols="12">
                <div style="height: 1px; background-color: #e0e0e0;"></div>
              </v-col>
            </v-row>
          </template>
        </v-card>
      </template>

      <!-- Monitor Commissions Divider -->
      <div v-if="!isSatelliteMonitor" style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white; border-radius: 6px; margin-top: 28px;">
        <div style="width: 25%; font-size: 16px; font-weight: 600;">Monitor Commissions</div>
        <div style="width: 25%; font-size: 14px;">Payment Date: {{ periodOptions.find(p => p.value === selectedPeriod)?.payment_date?.split('T')[0] || 'N/A' }}</div>
        <div style="width: 25%; font-size: 14px;">Period: {{ periodOptions.find(p => p.value === selectedPeriod)?.text || 'N/A' }}</div>
        <div style="width: 25%;">
          <v-btn
            style="text-transform: none; font-size: 14px; background-color: transparent; color: white; height: 32px; padding: 0 12px; border-radius: 4px;"
            @click="toggleMonitorDetails"
          >
            <span style="font-weight: 500; margin-right: 4px;">View Details</span>
            <v-icon small>mdi-chevron-down</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Monitor Commissions Info Table Card (expandable/collapsible) -->
      <v-card v-if="!isSatelliteMonitor" class="elevation-2" style="margin-top: 16px; padding: 8px 16px; border-radius: 6px; background-color: white;">
        <v-table density="compact" style="width: 100%; table-layout: fixed;">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <!-- Morning Commissions Section -->
          <thead>
            <tr v-if="showMonitorDetails">
              <th colspan="5" style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Morning Commissions</th>
            </tr>
            <tr v-else>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Morning Commissions</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Sales USD</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">%</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Commission COP</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">
                <v-icon icon="mdi-swap-vertical" size="small" />
              </th>
            </tr>
            <tr v-if="showMonitorDetails">
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Stage Name</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Sales USD</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">%</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Commission COP</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Actions</th>
            </tr>
          </thead>
          <tbody v-if="showMonitorDetails">
            <tr v-for="model in filteredModels.filter(m => {
              const loc = m.location_at_payment !== null && m.location_at_payment !== undefined
                ? m.location_at_payment
                : m.location_id;
              return loc === 1;
            })" :key="model.id">
              <td style="font-size: 14px; padding: 4px 8px;">{{ model.stageName || 'Unknown' }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">{{ formatCurrency(getModelSalesUSD(model), 'USD') }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">2%</td>
              <td style="font-size: 14px; padding: 4px 8px;">${{ formatCurrency(Math.floor(getModelSalesUSD(model) * usdXe * 0.02), 'COP', true) }} COP</td>
              <td>
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-dots-vertical"
                      v-bind="props"
                      size="x-small"
                      variant="text"
                      density="compact"
                      :disabled="isPeriodLocked"
                      :style="{ opacity: isPeriodLocked ? 0.5 : 1, pointerEvents: isPeriodLocked ? 'none' : 'auto' }"
                    ></v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="moveModel(model, 1)">
                      <v-list-item-title>Move to Morning</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="moveModel(model, 2)">
                      <v-list-item-title>Move to Evening</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="moveModel(model, 3)">
                      <v-list-item-title>Move to Night</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
          <tbody>
            <tr>
              <td :style="`font-size: 14px; padding: 4px 8px; ${showMonitorDetails ? 'font-weight: 700;' : ''}`">Total</td>
              <td style="font-size: 14px; padding: 4px 8px;">{{ formatCurrency(morningTotalSales, 'USD') }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">2%</td>
              <td :style="`font-size: 14px; padding: 4px 8px; ${showMonitorDetails ? 'font-weight: 700;' : ''}`">
                ${{ formatCurrency(Math.floor(morningCommission), 'COP', true) }} COP
              </td>
            </tr>
          </tbody>

          <!-- Evening Commissions Section -->
          <thead>
            <tr v-if="showMonitorDetails">
              <th colspan="5" style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Evening Commissions</th>
            </tr>
            <tr v-else>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Evening Commissions</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Sales USD</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">%</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Commission COP</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">
                <v-icon icon="mdi-swap-vertical" size="small" />
              </th>
            </tr>
            <tr v-if="showMonitorDetails">
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Stage Name</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Sales USD</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">%</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Commission COP</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Actions</th>
            </tr>
          </thead>
          <tbody v-if="showMonitorDetails">
            <tr v-for="model in filteredModels.filter(m => {
              const loc = m.location_at_payment !== null && m.location_at_payment !== undefined
                ? m.location_at_payment
                : m.location_id;
              return loc === 2;
            })" :key="model.id">
              <td style="font-size: 14px; padding: 4px 8px;">{{ model.stageName || 'Unknown' }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">{{ formatCurrency(getModelSalesUSD(model), 'USD') }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">2%</td>
              <td style="font-size: 14px; padding: 4px 8px;">${{ formatCurrency(Math.floor(getModelSalesUSD(model) * usdXe * 0.02), 'COP', true) }} COP</td>
              <td>
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-dots-vertical"
                      v-bind="props"
                      size="x-small"
                      variant="text"
                      density="compact"
                      :disabled="isPeriodLocked"
                      :style="{ opacity: isPeriodLocked ? 0.5 : 1, pointerEvents: isPeriodLocked ? 'none' : 'auto' }"
                    ></v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="moveModel(model, 1)">
                      <v-list-item-title>Move to Morning</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="moveModel(model, 2)">
                      <v-list-item-title>Move to Evening</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="moveModel(model, 3)">
                      <v-list-item-title>Move to Night</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
          <tbody>
            <tr>
              <td :style="`font-size: 14px; padding: 4px 8px; ${showMonitorDetails ? 'font-weight: 700;' : ''}`">Total</td>
              <td style="font-size: 14px; padding: 4px 8px;">{{ formatCurrency(eveningTotalSales, 'USD') }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">2%</td>
              <td :style="`font-size: 14px; padding: 4px 8px; ${showMonitorDetails ? 'font-weight: 700;' : ''}`">
                ${{ formatCurrency(Math.floor(eveningCommission), 'COP', true) }} COP
              </td>
            </tr>
          </tbody>

          <!-- Night Commissions Section -->
          <thead>
            <tr v-if="showMonitorDetails">
              <th colspan="5" style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Night Commissions</th>
            </tr>
            <tr v-else>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Night Commissions</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Sales USD</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">%</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">Commission COP</th>
              <th style="font-size: 14px; font-weight: 700; padding: 6px 8px;">
                <v-icon icon="mdi-swap-vertical" size="small" />
              </th>
            </tr>
            <tr v-if="showMonitorDetails">
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Stage Name</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Sales USD</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">%</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Commission COP</th>
              <th style="font-size: 12px; font-weight: 600; text-align: left; padding: 4px 8px;">Actions</th>
            </tr>
          </thead>
          <tbody v-if="showMonitorDetails">
            <tr v-for="model in filteredModels.filter(m => {
              const loc = m.location_at_payment !== null && m.location_at_payment !== undefined
                ? m.location_at_payment
                : m.location_id;
              return loc === 3;
            })" :key="model.id">
              <td style="font-size: 14px; padding: 4px 8px;">{{ model.stageName || 'Unknown' }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">{{ formatCurrency(getModelSalesUSD(model), 'USD') }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">2%</td>
              <td style="font-size: 14px; padding: 4px 8px;">${{ formatCurrency(Math.floor(getModelSalesUSD(model) * usdXe * 0.02), 'COP', true) }} COP</td>
              <td>
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-dots-vertical"
                      v-bind="props"
                      size="x-small"
                      variant="text"
                      density="compact"
                      :disabled="isPeriodLocked"
                      :style="{ opacity: isPeriodLocked ? 0.5 : 1, pointerEvents: isPeriodLocked ? 'none' : 'auto' }"
                    ></v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="moveModel(model, 1)">
                      <v-list-item-title>Move to Morning</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="moveModel(model, 2)">
                      <v-list-item-title>Move to Evening</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="moveModel(model, 3)">
                      <v-list-item-title>Move to Night</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
          <tbody>
            <tr>
              <td :style="`font-size: 14px; padding: 4px 8px; ${showMonitorDetails ? 'font-weight: 700;' : ''}`">Total</td>
              <td style="font-size: 14px; padding: 4px 8px;">{{ formatCurrency(nightTotalSales, 'USD') }}</td>
              <td style="font-size: 14px; padding: 4px 8px;">2%</td>
              <td :style="`font-size: 14px; padding: 4px 8px; ${showMonitorDetails ? 'font-weight: 700;' : ''}`">
                ${{ formatCurrency(Math.floor(nightCommission), 'COP', true) }} COP
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

      <!-- Hidden ExportPayments component -->
      <ExportPayments
        v-show="false"
        ref="exportPayments"
        :models="filteredModels"
        :usdXe="usdXe"
        :eurXe="eurXe"
        :period="selectedPeriod"
        :showCedula="true"
      />
    </template>
  <v-btn
    v-if="isEditingCommissions"
    class="btn-secondary"
    @click="toggleEditCommissions"
    style="position: fixed; bottom: 24px; right: 24px; z-index: 999; padding: 0 16px; height: 36px; background-color: #1976d2; color: white; border: none;"
  >
    <v-icon size="20" color="white">mdi-percent</v-icon>
  </v-btn>
  </div>
</template>

<script>
import secureAxios from '@/utils/secureAxios';

import ExportPayments from '@/components/ExportPayments.vue';
import { toast } from 'vue3-toastify';

export default {
  components: {
    ExportPayments
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')),
      models: [],
      selectedPeriod: null,
      periodOptions: [],
      activeCard: null,
      activeTab: (JSON.parse(localStorage.getItem('user'))?.role === 'satellite_monitor') ? 'satellite' : 'studio',
      usdXe: null, // No default
      cbXe: null, // Dedicated Chaturbate TRM
      eurXe: null, // No default
      loading: true,
      modelFilter: null,
      selectedModels: [],
      downloadFormat: 'pdf',
      isPeriodLocked: false,
      isCollapsed: false,
      isEditingCommissions: false,
      lastExported: 'N/A',
      sortOption: 'Name A → Z',
      sortOptions: [
        'Earnings High',
        'Earnings Low',
        'Name A → Z',
        'Name Z → A',
        'Artistic A → Z',
        'Artistic Z → A'
      ],
      isAdmin: false,
      showMonitorDetails: false,
    };
  },
  computed: {
    isSatelliteMonitor() {
      return this.user?.role === 'satellite_monitor';
    },
    canPersistTrmMaster() {
      return Number(this.user?.studio_id) === 1 && !this.isSatelliteMonitor;
    },
    canEditPayments() {
      // Satellite monitor gets “admin-like” editing for Satellite view, but still cannot lock/unlock.
      return this.isAdmin || this.isSatelliteMonitor;
    },
    hasAnyUnlockedModels() {
      // Allows satellite monitor to edit commissions if there is at least one unlocked model visible
      return Array.isArray(this.filteredModels) && this.filteredModels.some(m => !m.isLocked);
    },
    selectedPeriodIndex() {
      return this.periodOptions.findIndex(period => Number(period.value) === Number(this.selectedPeriod));
    },
    canGoToPreviousPeriod() {
      return this.selectedPeriodIndex > 0;
    },
    canGoToNextPeriod() {
      return this.selectedPeriodIndex !== -1 && this.selectedPeriodIndex < this.periodOptions.length - 1;
    },
    filteredModels() {
      let filtered = Array.isArray(this.models) ? [...this.models] : [];

      // Only filter by tab and selectedModels, do NOT filter out models with no sales/adjustments/subsidy
      if (this.isSatelliteMonitor) {
        if (this.activeTab !== 'satellite') this.activeTab = 'satellite';
        filtered = filtered.filter(model =>
          model.location && model.location.toLowerCase() === 'satellite'
        );
      } else {
        // Safety: Lata tab removed. If something still sets it, fall back to Studio.
        if (this.activeTab && this.activeTab.toLowerCase() === 'lata') {
          this.activeTab = 'studio';
        }
        if (this.activeTab === 'studio') {
          filtered = filtered.filter(model =>
            ['Morning', 'Evening', 'Night'].includes(model.location)
          );
        } else {
          filtered = filtered.filter(model =>
            model.location && model.location.toLowerCase() === this.activeTab.toLowerCase()
          );
        }
      }

      if (this.selectedModels.length > 0) {
        filtered = filtered.filter(model =>
          this.selectedModels.includes(model.fullName)
        );
      }

      return this.sortModels(this.sortOption, filtered);
    },
    totalGrossEarnings() {
      // Sum up gross_earnings from each model, without any additional TRM conversion or mapping
      return this.models.reduce((total, model) => {
        const earnings = parseFloat(model.gross_earnings || 0);
        return total + earnings;
      }, 0);
    },
    totalAdjustments() {
      return this.filteredModels.reduce((sum, model) =>
        sum + (model.adjustments.reduce((aSum, adj) => adj.nextPeriod ? aSum : aSum + ((adj.copTotal || 0) * adj.sign), 0)), 0);
    },
    totalDeductions() {
      return this.filteredModels.reduce((sum, model) =>
        sum + Math.abs(model.deductions.reduce((dSum, ded) => ded.nextPeriod ? dSum : dSum + ((ded.copTotal || 0) * ded.sign), 0)), 0);
    },
    netPaymentTotal() {
      return this.totalGrossEarnings + this.totalAdjustments - this.totalDeductions;
    },
    morningCommission() {
      // Use location_at_payment if present, fallback to location_id, fallback to location string
      return this.filteredModels
        .filter(model => {
          const loc = model.location_at_payment !== null && model.location_at_payment !== undefined
            ? model.location_at_payment
            : (model.location_id !== undefined ? model.location_id : null);
          // 1 = Morning
          return loc === 1;
        })
        .reduce((sum, model) =>
          sum + Math.floor(model.earnings.reduce((eSum, earn) => {
            const trm = this.getEarningTrmRate(earn);
            return eSum + ((earn.earnings || 0) * 0.02 * trm);
          }, 0))
        , 0);
    },
    eveningCommission() {
      return this.filteredModels
        .filter(model => {
          const loc = model.location_at_payment !== null && model.location_at_payment !== undefined
            ? model.location_at_payment
            : (model.location_id !== undefined ? model.location_id : null);
          // 2 = Evening
          return loc === 2;
        })
        .reduce((sum, model) =>
          sum + Math.floor(model.earnings.reduce((eSum, earn) => {
            const trm = this.getEarningTrmRate(earn);
            return eSum + ((earn.earnings || 0) * 0.02 * trm);
          }, 0))
        , 0);
    },
    nightCommission() {
      return this.filteredModels
        .filter(model => {
          const loc = model.location_at_payment !== null && model.location_at_payment !== undefined
            ? model.location_at_payment
            : (model.location_id !== undefined ? model.location_id : null);
          // 3 = Night
          return loc === 3;
        })
        .reduce((sum, model) =>
          sum + Math.floor(model.earnings.reduce((eSum, earn) => {
            const trm = this.getEarningTrmRate(earn);
            return eSum + ((earn.earnings || 0) * 0.02 * trm);
          }, 0))
        , 0);
    },
    totalCommission() {
      return Math.floor(this.morningCommission + this.eveningCommission + this.nightCommission);
    },
    morningTotalSales() {
      return this.filteredModels
        .filter(model => {
          const loc = model.location_at_payment !== null && model.location_at_payment !== undefined
            ? model.location_at_payment
            : (model.location_id !== undefined ? model.location_id : null);
          return loc === 1;
        })
        .reduce((sum, model) =>
          sum + model.earnings.reduce((eSum, earn) => {
            return eSum + ((earn.earnings || 0));
          }, 0), 0);
    },
    eveningTotalSales() {
      return this.filteredModels
        .filter(model => {
          const loc = model.location_at_payment !== null && model.location_at_payment !== undefined
            ? model.location_at_payment
            : (model.location_id !== undefined ? model.location_id : null);
          return loc === 2;
        })
        .reduce((sum, model) =>
          sum + model.earnings.reduce((eSum, earn) => {
            return eSum + ((earn.earnings || 0));
          }, 0), 0);
    },
    nightTotalSales() {
      return this.filteredModels
        .filter(model => {
          const loc = model.location_at_payment !== null && model.location_at_payment !== undefined
            ? model.location_at_payment
            : (model.location_id !== undefined ? model.location_id : null);
          return loc === 3;
        })
        .reduce((sum, model) =>
          sum + model.earnings.reduce((eSum, earn) => {
            return eSum + ((earn.earnings || 0));
          }, 0), 0);
    },
    moveModelToShift(item, shiftId) {
    }
  },
  watch: {
    usdXe(newVal) {
      if (newVal !== null && this.models.length > 0) {
        this.recalculateAdjustments();
      }
    },
    cbXe(newVal) {
      if (newVal !== null && this.models.length > 0) {
        this.recalculateAdjustments();
      }
    },
    eurXe(newVal) {
      if (newVal !== null && this.models.length > 0) {
        this.recalculateAdjustments();
      }
    }
  },
  methods: {
    isCbPlatform(platform) {
      const normalized = String(platform || '').trim().toLowerCase();
      return normalized.includes('chaturbate') || normalized === 'cam4';
    },
    getEarningTrmRate(earn) {
      const currency = String(earn?.currency || '').toUpperCase();
      if (currency === 'USD') {
        return this.isCbPlatform(earn?.platform) ? (this.cbXe ?? this.usdXe) : this.usdXe;
      }
      if (currency === 'EUR') return this.eurXe;
      if (currency === 'COP') return 1;
      return null;
    },
    getEarningTrm(earn) {
      const rate = this.getEarningTrmRate(earn);
      return rate === null || rate === undefined ? '—' : rate;
    },
    getAdjustmentTrmRate(item) {
      const currency = String(item?.currency || '').toUpperCase();
      if (currency === 'USD') {
        return this.normalizeCardGroup(item?.site_group) === 'cb' ? (this.cbXe ?? this.usdXe) : this.usdXe;
      }
      if (currency === 'EUR') return this.eurXe;
      if (currency === 'COP') return 1;
      return null;
    },
    moveModel(model, newLocation) {
      const payload = {
        modelId: model.id,
        locationId: newLocation,
        periodId: this.selectedPeriod,
        studioId: this.selectedStudioId || this.selectedStudio || model.studio_id
      };
      // Add debug log before posting
      secureAxios.post('/api/payments/update-location-at-payment', payload)
        .then((res) => {
          // Reactivity-safe update of location_at_payment
          const targetModel = this.models.find(m => m.id === model.id);
          if (targetModel) {
            targetModel.location_at_payment = newLocation;
            this.$toast.success('Modelo movido exitosamente');
          }
        })
        .catch(err => {
          console.error('❌ Failed to move model:', err?.message || err);
        });
    },
    toggleMonitorDetails() {
      this.showMonitorDetails = !this.showMonitorDetails;
    },

    getWizCalcStartPeriodId() {
      return Number(this.periodOptions.find((p) => p.text === '14-Narwhal-2026')?.value || 14);
    },
    // Narwhal+ uses Wiz-style calc behavior (apply commission first, then convert)
    isWizMode() {
      return Number(this.selectedPeriod) >= this.getWizCalcStartPeriodId();
    },
    getSiteBonusRuleStartPeriodId() {
      return Number(this.periodOptions.find((p) => p.text === '14-Narwhal-2026')?.value || 14);
    },
    isStudioModel(model) {
      return String(model?.location || '').trim().toLowerCase() !== 'satellite';
    },
    getAdjustmentPayoutRate(item, model) {
      const type = String(item?.type || '').trim().toLowerCase();
      const isSiteBonus = type === 'site bonus' || type === 'bonus';
      const isStudioBonus = type === 'studio bonus';
      const hasOverride = item?.commission_percent !== null &&
        item?.commission_percent !== undefined &&
        item?.commission_percent !== '';
      const overridePercent = Number(item?.commission_percent);
      if ((isSiteBonus || isStudioBonus) && hasOverride && Number.isFinite(overridePercent)) {
        return overridePercent / 100;
      }
      if (
        isSiteBonus &&
        this.isStudioModel(model) &&
        Number(this.selectedPeriod) >= this.getSiteBonusRuleStartPeriodId()
      ) {
        return 0.8;
      }
      return 1;
    },

    calcEarningsCopTotal(earn) {
      const trmRate = this.getEarningTrmRate(earn);
      if (trmRate === null || trmRate === undefined) return null;

      const gross = Number(earn.gross_earnings ?? earn.earnings ?? 0);
      const commissionPct = Number(earn.commission ?? 0) / 100;

      // Narwhal+: commission applied in source currency first, then converted
      if (this.isWizMode()) {
        // Do the math in integer cents to avoid floating-point rounding errors
        // Example: 555.55 * 50% => 277.78 (not 277.77)
        const grossCents = Math.round(gross * 100);
        const commissionInt = Math.round(commissionPct * 100); // 0.50 -> 50
        const netCents = Math.round((grossCents * commissionInt) / 100);
        const netInCurrency = netCents / 100;
        return netInCurrency * trmRate;
      }

      // Legacy periods
      return gross * trmRate * commissionPct;
    },

    calcAdjOrDedCopTotals(item, model = null) {
      const trmRate = this.getAdjustmentTrmRate(item);
      if (trmRate === null || trmRate === undefined) return { fullCopTotal: null, copTotal: null };

      const amount = Number(item.amount || 0);
      const quotas = Number(item.quotas || 1) || 1;
      const payoutRate = this.getAdjustmentPayoutRate(item, model);
      const currency = String(item?.currency || '').toUpperCase();

      if (this.isWizMode() && (currency === 'USD' || currency === 'EUR')) {
        const fullSourceTotal = Math.round(amount * payoutRate * 100) / 100;
        const sourceTotal = Math.round((amount / quotas) * payoutRate * 100) / 100;
        return {
          fullCopTotal: fullSourceTotal * trmRate,
          copTotal: sourceTotal * trmRate
        };
      }

      const fullCopTotal = amount * trmRate * payoutRate;
      const copTotal = fullCopTotal / quotas;
      return { fullCopTotal, copTotal };
    },
    async fetchPeriods() 
    {
      try {
        const response = await secureAxios.get('/api/payments/periods');
        this.periodOptions = response.data.map(period => ({
          value: period.id,
          text: period.name,
          payment_date: period.payment_date
        }));
        this.periodOptions.sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

        const todayStr = new Date().toISOString().split('T')[0];

        const currentPeriod = this.periodOptions.find(period => {
          const cutoff = new Date(period.payment_date);
          cutoff.setDate(cutoff.getDate() + 1);
          const cutoffStr = cutoff.toISOString().split('T')[0];
          return todayStr < cutoffStr;
        }) || this.periodOptions[this.periodOptions.length - 1];
        this.selectedPeriod = currentPeriod.value;
        await this.fetchModels();
        this.loading = false;
      } catch (error) {
        console.error('❌ Error fetching periods:', error?.message || error);
        toast.error('Failed to fetch periods');
        this.loading = false;
      }
    },
    async fetchModels(options = {}) {
      if (!this.selectedPeriod) return;
      try {
        const response = await secureAxios.get('/api/payments/models', {
          params: {
            periodId: this.selectedPeriod,
            ...(options.refresh ? { refresh: '1' } : {})
          }
        });
        this.models = response.data.models.map(model => {
          const earnings = model.earnings.map(earn => {
            const commission = earn.commission !== null && earn.commission !== undefined
              ? earn.commission
              : (earn.platform === 'Flirt4Free' && this.selectedPeriod <= 18 ? 100 : 50);

            const mapped = {
              ...earn,
              commission,
              // keep a consistent field for the source-currency gross
              gross_earnings: earn.gross_earnings ?? earn.earnings
            };

            return {
              ...mapped,
              copTotal: this.calcEarningsCopTotal(mapped)
            };
          });
          const adjustments = model.adjustments.map(adj => {
            const { fullCopTotal, copTotal } = this.calcAdjOrDedCopTotals(adj, model);
            return { ...adj, fullCopTotal, copTotal, sign: 1 };
          });
          const deductions = model.deductions.map(ded => {
            const { fullCopTotal, copTotal } = this.calcAdjOrDedCopTotals(ded, model);
            return { ...ded, fullCopTotal, copTotal, sign: -1 };
          });
          const pendingApplied = Number(model?.pending_global?.applied ?? model?.applied_pending_amount ?? 0);
          const pendingCurrent = Number(model?.pending_global?.pending ?? model?.pending_amount ?? 0);
          const sectionSummaries = this.normalizeSectionSummaries(model?.section_summaries);
          const mappedModel = {
            ...model,
            earnings,
            adjustments,
            deductions,
            totalEarnings: null,
            pending_global: { applied: pendingApplied, pending: pendingCurrent },
            section_summaries: sectionSummaries,
            minimum_payment_applied: Boolean(model?.minimum_payment_applied),
            minimum_payment_required: Number(model?.minimum_payment_required || 0),
            minimum_payment_carry: Number(model?.minimum_payment_carry || 0)
          };
          // Set default location_at_payment if not present
          if (mappedModel.location_at_payment === null || mappedModel.location_at_payment === undefined) {
            const loc = mappedModel.location?.toLowerCase();
            if (loc === 'morning') mappedModel.location_at_payment = 1;
            else if (loc === 'evening') mappedModel.location_at_payment = 2;
            else if (loc === 'night') mappedModel.location_at_payment = 3;
          }
          return mappedModel;
        }) || [];
        this.isPeriodLocked = response.data.isLocked || false;
        this.isAdmin = response.data.isAdmin || false;
        this.usdXe = response.data.trmUSD !== undefined ? response.data.trmUSD : this.usdXe;
        this.cbXe = response.data.trmCB !== undefined ? response.data.trmCB : (this.cbXe ?? this.usdXe);
        this.eurXe = response.data.trmEUR !== undefined ? response.data.trmEUR : this.eurXe;
        this.models.forEach(model => {
          this.addSubsidyAdjustment(model);
          this.recalculateTotal(model);
        });
      } catch (error) {
        console.error('❌ Error fetching models:', error?.message || error);
        toast.error('Failed to fetch models');
        this.models = [];
      }
    },
    sortModels(option, modelsToSort = this.models) {
      this.sortOption = option;
      const sortedModels = [...modelsToSort];
      switch (option) {
        case 'Earnings High':
          sortedModels.sort((a, b) => (b.net_payment || 0) - (a.net_payment || 0));
          break;
        case 'Earnings Low':
          sortedModels.sort((a, b) => (a.net_payment || 0) - (b.net_payment || 0));
          break;
        case 'Name A → Z':
          sortedModels.sort((a, b) => a.fullName.localeCompare(b.fullName));
          break;
        case 'Name Z → A':
          sortedModels.sort((a, b) => b.fullName.localeCompare(a.fullName));
          break;
        case 'Artistic A → Z':
          sortedModels.sort((a, b) => a.stageName.localeCompare(b.stageName));
          break;
        case 'Artistic Z → A':
          sortedModels.sort((a, b) => b.stageName.localeCompare(a.stageName));
          break;
      }
      return sortedModels;
    },
    formatCurrency(value, currency, hideSymbol = false) {
      if (value === null || value === undefined || isNaN(value)) return 'N/A';
      if (currency !== 'COP') {
        return new Intl.NumberFormat('en-US', {
          style: hideSymbol ? 'decimal' : 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }
      const formatted = Number(value);
      const hasDecimals = formatted % 1 !== 0;
      return new Intl.NumberFormat('en-US', {
        style: hideSymbol ? 'decimal' : 'currency',
        currency: 'COP',
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0
      }).format(value);
    },
    displayCopForSummary(value) {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return 0;
      return Math.floor(n);
    },
    displayCopForPositiveTotal(value) {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return 0;
      return n >= 0 ? Math.round(n) : Math.floor(n);
    },
    displayCommission(commission) {
      if (commission === null || commission === undefined || isNaN(commission)) return '0%';
      return `${commission}%`;
    },
    displayAdjustmentCommission(item, model) {
      const percent = this.getAdjustmentCommissionPercent(item, model);
      return `${percent % 1 === 0 ? percent.toFixed(0) : percent}%`;
    },
    canEditAdjustmentCommission(item) {
      const type = String(item?.type || '').trim().toLowerCase();
      return type === 'site bonus' || type === 'studio bonus' || type === 'bonus';
    },
    getAdjustmentCommissionPercent(item, model) {
      const hasOverride = item?.commission_percent !== null &&
        item?.commission_percent !== undefined &&
        item?.commission_percent !== '';
      const overridePercent = Number(item?.commission_percent);
      if (this.canEditAdjustmentCommission(item) && hasOverride && Number.isFinite(overridePercent)) {
        return Math.round(overridePercent * 100) / 100;
      }
      const rate = this.getAdjustmentPayoutRate(item, model);
      return Math.round(Number(rate || 1) * 10000) / 100;
    },
    updateAdjustmentCommissionValue(model, item, value) {
      const parsed = Number(value);
      item.commission_percent = Number.isFinite(parsed) ? parsed : null;
      const { fullCopTotal, copTotal } = this.calcAdjOrDedCopTotals(item, model);
      item.fullCopTotal = fullCopTotal;
      item.copTotal = copTotal;
      this.recalculateTotal(model);
    },
    normalizeCardGroup(groupValue) {
      const group = String(groupValue || '').trim().toLowerCase();
      return group === 'cb' ? 'cb' : 'main';
    },
    getSubsidyAmountForValue(subsidyValue) {
      const raw = String(subsidyValue || '').trim();
      if (raw === 'Half' || raw === 'Half - CB') return 15000;
      if (raw === 'Full' || raw === 'Full - CB') return 30000;
      return 0;
    },
    getSubsidyGroupForModel(model) {
      const subsidyValue = String(model?.subsidy || '').trim();
      if (subsidyValue === 'Full - CB' || subsidyValue === 'Half - CB') return 'cb';
      return this.normalizeCardGroup(model?.subsidy_group);
    },
    getSplitStartPeriodId() {
      return Number(this.periodOptions.find((p) => p.text === '4-Dolphin-2026')?.value || 30);
    },
    isSplitPendingEnabled() {
      return Number(this.selectedPeriod) >= this.getSplitStartPeriodId();
    },
    getModelPaymentCards(model) {
      const allEarnings = Array.isArray(model.earnings) ? model.earnings : [];
      const periodId = Number(this.selectedPeriod);
      const splitStartPeriodId = this.getSplitStartPeriodId();
      const splitEnabled = periodId >= splitStartPeriodId;
      const cbEarnings = allEarnings.filter((earn) =>
        String(earn.platform || '').toLowerCase().includes('chaturbate')
      );
      const allAdjustments = Array.isArray(model.adjustments) ? model.adjustments : [];
      const allDeductions = Array.isArray(model.deductions)
        ? model.deductions.filter((ded) => !ded.nextPeriod)
        : [];

      // Missing/null site_group is treated as MAIN by design.
      const cbAdjustments = allAdjustments.filter((adj) => this.normalizeCardGroup(adj.site_group) === 'cb');
      const mainAdjustments = allAdjustments.filter((adj) => this.normalizeCardGroup(adj.site_group) !== 'cb');
      const cbDeductions = allDeductions.filter((ded) => this.normalizeCardGroup(ded.site_group) === 'cb');
      const mainDeductions = allDeductions.filter((ded) => this.normalizeCardGroup(ded.site_group) !== 'cb');

      const subsidyGroup = this.getSubsidyGroupForModel(model);
      const retentionGroup = this.normalizeCardGroup(model.retention_group);
      const hasRetention = model.retention_status === 'Yes' || model.retention_status === 'Always';

      const hasCbActivity = cbEarnings.length > 0 ||
        cbAdjustments.length > 0 ||
        cbDeductions.length > 0 ||
        subsidyGroup === 'cb' ||
        (retentionGroup === 'cb' && hasRetention);

      const hasSplitCards = splitEnabled && hasCbActivity;

      if (!hasSplitCards) {
        const singleCard = {
          card_key: `main-${model.id}`,
          title: 'MAIN',
          group: 'main',
          splitEnabled,
          earningsTitle: 'Site Earnings',
          paymentDate: model.main_payment_date || model.paymentDate || 'N/A',
          earnings: allEarnings,
          adjustments: allAdjustments,
          deductions: allDeductions,
          retentionAmount: 0,
          applied_pending_amount: 0,
          pending_amount: 0,
          net_payment: 0
        };
        singleCard.retentionAmount = this.getRetentionAmountForCard(model, singleCard);
        return this.applyGlobalPendingToCards(model, [singleCard]);
      }

      const mainEarnings = allEarnings.filter(
        (earn) => !String(earn.platform || '').toLowerCase().includes('chaturbate')
      );

      const hasMainActivity =
        mainEarnings.length > 0 ||
        mainAdjustments.length > 0 ||
        mainDeductions.length > 0 ||
        subsidyGroup === 'main' ||
        (retentionGroup === 'main' && hasRetention);

      const cards = [];

      if (hasMainActivity) {
        cards.push({
          card_key: `main-${model.id}`,
          title: 'MAIN',
          group: 'main',
          splitEnabled: true,
          earningsTitle: 'Site Earnings',
          paymentDate: model.main_payment_date || model.paymentDate || 'N/A',
          earnings: mainEarnings,
          adjustments: mainAdjustments,
          deductions: mainDeductions,
          retentionAmount: 0,
          applied_pending_amount: 0,
          pending_amount: 0,
          net_payment: 0
        });
      }

      cards.push({
        card_key: `cb-${model.id}`,
        title: 'CHaturbate',
        group: 'cb',
        splitEnabled: true,
        earningsTitle: 'Chaturbate Earnings',
        paymentDate: model.cb_payment_date || model.main_payment_date || model.paymentDate || 'N/A',
        earnings: cbEarnings,
        adjustments: cbAdjustments,
        deductions: cbDeductions,
        retentionAmount: 0,
        applied_pending_amount: 0,
        pending_amount: 0,
        net_payment: 0
      });

      cards.forEach((card) => {
        card.retentionAmount = this.getRetentionAmountForCard(model, card);
      });

      const sortedCards = cards.sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate));
      const fallbackCards = this.applyGlobalPendingToCards(model, sortedCards.map((card) => ({ ...card })));
      const mappedFromSectionSummaries = this.applySectionSummariesToCards(model, fallbackCards);
      return mappedFromSectionSummaries || fallbackCards;
    },
    hasAdjustmentsInCard(card) {
      return Array.isArray(card.adjustments) && card.adjustments.length > 0;
    },
    hasDeductionsInCard(card) {
      return Array.isArray(card.deductions) && card.deductions.length > 0;
    },
    getGrossEarningsForCard(card) {
      if (!Array.isArray(card.earnings)) return 0;
      if (this.isWizMode()) {
        // Wiz: keep COP decimals through section math; round only at final display.
        return card.earnings.reduce((sum, earn) => sum + Number(earn.copTotal || 0), 0);
      }
      return card.earnings.reduce((sum, earn) => sum + Math.floor(earn.copTotal || 0), 0);
    },
    getRetentionAmountForCard(model, card) {
      if (!(model.retention_status === 'Yes' || model.retention_status === 'Always')) return 0;
      if (!card.splitEnabled) {
        return Math.floor(this.getGrossEarningsForCard(card) * 0.04);
      }
      const retentionGroup = this.normalizeCardGroup(model.retention_group);
      if (retentionGroup !== card.group) return 0;
      return Math.floor(this.getGrossEarningsForCard(card) * 0.04);
    },
    getRawNetPaymentForCard(card) {
      const gross = this.getGrossEarningsForCard(card);
      const adjustmentsTotal = (card.adjustments || []).reduce((sum, adj) => {
        if (adj.nextPeriod) return sum;
        const lineValue = Number(adj.copTotal || 0) * (adj.sign ?? 1);
        return sum + (this.isWizMode() ? lineValue : Math.floor(lineValue));
      }, 0);
      const deductionsTotal = (card.deductions || []).reduce((sum, ded) => {
        if (ded.nextPeriod) return sum;
        const lineValue = Number(ded.copTotal || 0) * (ded.sign ?? -1);
        return sum + (this.isWizMode() ? lineValue : Math.floor(lineValue));
      }, 0);
      return gross + adjustmentsTotal + deductionsTotal - (card.retentionAmount || 0);
    },
    getGlobalAppliedPending(model) {
      const value = Number(model?.pending_global?.applied ?? model?.applied_pending_amount ?? 0);
      return Number.isFinite(value) ? value : 0;
    },
    normalizeSectionSummary(section) {
      if (!section || typeof section !== 'object') return null;
      return {
        ...section,
        applied_pending_amount: Number(section.applied_pending_amount || 0),
        pending_amount: Number(section.pending_amount || 0),
        net_payment: Number(section.net_payment || 0),
        minimum_payment_applied: Boolean(section.minimum_payment_applied),
        minimum_payment_required: Number(section.minimum_payment_required || 0)
      };
    },
    normalizeSectionSummaries(sectionSummaries) {
      if (!sectionSummaries || typeof sectionSummaries !== 'object') return null;
      return {
        main: this.normalizeSectionSummary(sectionSummaries.main),
        cb: this.normalizeSectionSummary(sectionSummaries.cb)
      };
    },
    applySectionSummariesToCards(model, cards) {
      const summaries = this.normalizeSectionSummaries(model?.section_summaries);
      if (!summaries || (!summaries.main && !summaries.cb)) return null;

      let hasMappedSummary = false;
      const mappedCards = cards.map((card) => {
        const summary = summaries[this.normalizeCardGroup(card?.group)];
        if (!summary) return card;
        hasMappedSummary = true;
        return {
          ...card,
          applied_pending_amount: Number(summary.applied_pending_amount || 0),
          pending_amount: Number(summary.pending_amount || 0),
          net_payment: Number(summary.net_payment || 0),
          minimum_payment_applied: Boolean(summary.minimum_payment_applied),
          minimum_payment_required: Number(summary.minimum_payment_required || 0)
        };
      });

      return hasMappedSummary ? mappedCards : null;
    },
    applyGlobalPendingToCards(model, cards) {
      if (!Array.isArray(cards)) return [];

      // Default behavior: no pending lines, raw card net as before.
      cards.forEach((card) => {
        const rawNet = this.getRawNetPaymentForCard(card);
        card.applied_pending_amount = 0;
        card.pending_amount = 0;
        card.net_payment = rawNet;
        card.minimum_payment_applied = false;
        card.minimum_payment_required = 0;
      });

      const splitPendingEnabled = this.isSplitPendingEnabled() && cards.some((card) => card?.splitEnabled);
      if (!splitPendingEnabled) return cards;

      // Global debt chain display:
      // Process cards in payment-date order and carry debt forward card-by-card.
      let runningPending = this.getGlobalAppliedPending(model); // signed (negative debt or positive carry)
      cards.forEach((card) => {
        const rawNet = this.getRawNetPaymentForCard(card);
        const previousPending = Number(runningPending || 0);
        const afterPending = rawNet + previousPending;
        let newPending = 0;
        let payable = 0;
        const minimumRequired = 60000;

        if (afterPending <= 0) {
          newPending = afterPending;
          payable = 0;
        } else if (afterPending < minimumRequired) {
          newPending = afterPending;
          payable = 0;
          card.minimum_payment_applied = true;
          card.minimum_payment_required = minimumRequired;
        } else {
          newPending = 0;
          payable = afterPending;
        }

        card.applied_pending_amount = previousPending;
        card.pending_amount = newPending;
        card.net_payment = payable;

        runningPending = newPending;
      });

      return cards;
    },
    getEarningsHeaderStyle(card) {
      const isCb = String(card?.title || '').toLowerCase().includes('chaturbate');
      return {
        fontSize: '14px',
        fontWeight: '700',
        padding: '6px 8px',
        backgroundColor: isCb ? '#f2f2f2' : 'transparent'
      };
    },
    getModelHeaderStyle(model) {
      return {
        display: 'block',
        marginTop: '8px',
        padding: '10px 10px 6px 10px',
        borderRadius: '8px',
        background: 'linear-gradient(180deg, #eaf3ff 0%, #dcecff 100%)',
        border: '1px solid #c9dffb'
      };
    },
    setActiveCard(card) {
      this.activeCard = card;
    },
    viewModel() {
      if (this.modelFilter) {
        const fullName = this.modelFilter.split(' (')[0]; // Extract fullName before " ("
        const match = this.filteredModels.find(model => model.fullName === fullName);
        if (match && !this.selectedModels.includes(match.fullName)) {
          this.selectedModels.push(match.fullName);
        }
        this.modelFilter = null;
      }
    },
    removeModelChip(modelName) {
      this.selectedModels = this.selectedModels.filter(name => name !== modelName);
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    toggleDownloadFormat() {
      this.downloadFormat = this.downloadFormat === 'pdf' ? 'csv' : 'pdf';
    },
    async downloadData() {
      if (!this.$refs.exportPayments) {
        console.error('ExportPayments component not found');
        toast.error('Export component not found');
        return;
      }
      try {
        if (this.downloadFormat === 'pdf') {
          await this.$refs.exportPayments.downloadPDF();
          toast.success('PDF downloaded successfully');
        } else {
          await this.$refs.exportPayments.downloadCSV();
          toast.success('CSV downloaded successfully');
        }
        this.lastExported = new Date().toLocaleString('en-US', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('❌ Error during export:', error?.message || error);
        toast.error('Failed to download data');
      }
    },
    async setMainTrm() {
      if (!this.usdXe || (!this.isSatelliteMonitor && this.isPeriodLocked)) return;
      try {
        if (!this.canPersistTrmMaster) {
          this.recalculateAdjustments();
          toast.success('Main TRM updated for this session only');
          return;
        }
        await secureAxios.post('/api/payments/set-trm', { periodId: this.selectedPeriod, currency: 'USD', rate: this.usdXe });
        this.recalculateAdjustments();
        toast.success('Main TRM set successfully');
      } catch (error) {
        console.error('❌ Error setting Main Xe rate:', error?.message || error);
        toast.error('Failed to set Main TRM');
      }
    },
    async setCbTrm() {
      if (!this.cbXe || (!this.isSatelliteMonitor && this.isPeriodLocked)) return;
      try {
        if (!this.canPersistTrmMaster) {
          this.recalculateAdjustments();
          toast.success('Chaturbate TRM updated for this session only');
          return;
        }
        await secureAxios.post('/api/payments/set-trm', { periodId: this.selectedPeriod, currency: 'CB', rate: this.cbXe });
        this.recalculateAdjustments();
        toast.success('Chaturbate TRM set successfully');
      } catch (error) {
        console.error('❌ Error setting Chaturbate Xe rate:', error?.message || error);
        toast.error('Failed to set Chaturbate TRM');
      }
    },
    async setEurXe() {
      if (!this.eurXe || (!this.isSatelliteMonitor && this.isPeriodLocked)) return;
      try {
        if (!this.canPersistTrmMaster) {
          this.recalculateAdjustments();
          toast.success('EUR TRM updated for this session only');
          return;
        }
        await secureAxios.post('/api/payments/set-trm', { periodId: this.selectedPeriod, currency: 'EUR', rate: this.eurXe });
        this.recalculateAdjustments();
        toast.success('EUR TRM set successfully');
      } catch (error) {
        console.error('❌ Error setting EUR Xe rate:', error?.message || error);
        toast.error('Failed to set EUR TRM');
      }
    },
    recalculateAdjustments() {
      this.models = this.models.map(model => {
        const earnings = (model.earnings || []).map(earn => {
          const commission = earn.commission !== null && earn.commission !== undefined
            ? earn.commission
            : (earn.platform === 'Flirt4Free' && Number(this.selectedPeriod) <= 18 ? 100 : 50);

          const mapped = {
            ...earn,
            commission,
            // keep a consistent field for the source-currency gross
            gross_earnings: earn.gross_earnings ?? earn.earnings
          };

          return {
            ...mapped,
            copTotal: this.calcEarningsCopTotal(mapped)
          };
        });

        const adjustments = (model.adjustments || []).map(adj => {
          const { fullCopTotal, copTotal } = this.calcAdjOrDedCopTotals(adj, model);
          return { ...adj, fullCopTotal, copTotal, sign: 1 };
        });

        const deductions = (model.deductions || []).map(ded => {
          const { fullCopTotal, copTotal } = this.calcAdjOrDedCopTotals(ded, model);
          return { ...ded, fullCopTotal, copTotal, sign: -1 };
        });

        const updatedModel = { ...model, earnings, adjustments, deductions };
        this.recalculateTotal(updatedModel);
        return updatedModel;
      });
    },
    async viewPeriod() {
      await this.fetchModels({ refresh: true });
    },
    async goToPreviousPeriod() {
      if (!this.canGoToPreviousPeriod) return;
      this.selectedPeriod = this.periodOptions[this.selectedPeriodIndex - 1].value;
      this.setActiveCard('period');
      await this.fetchModels();
    },
    async goToNextPeriod() {
      if (!this.canGoToNextPeriod) return;
      this.selectedPeriod = this.periodOptions[this.selectedPeriodIndex + 1].value;
      this.setActiveCard('period');
      await this.fetchModels();
    },
    async toggleLock() {
      if (!this.isAdmin) {
        toast.error('Only admins can lock/unlock periods');
        return;
      }
      const action = this.isPeriodLocked ? 'unlock' : 'lock';
      if (confirm(`Are you sure you want to ${action} period ${this.selectedPeriod}?`)) {
        try {
          const newLockStatus = !this.isPeriodLocked;
          await secureAxios.post('/api/payments/lock-period', { 
            periodId: this.selectedPeriod, 
            lock: newLockStatus 
          });
          this.isPeriodLocked = newLockStatus;
          toast.success(`Period ${action}ed successfully`);
        } catch (error) {
          console.error('❌ Error toggling lock:', error?.message || error);
          toast.error(`Failed to ${action} period`);
        }
      }
    },
    recalculateTotal(model) {
      // Wiz-mode: sum raw line values, then truncate ONCE (towards zero)
      if (this.isWizMode()) {
        const sumEarnings = (model.earnings || []).reduce((sum, earn) => sum + Number(earn.copTotal || 0), 0);
        const sumAdjustments = (model.adjustments || []).reduce((sum, adj) => {
          if (adj.nextPeriod) return sum;
          const sign = adj.sign !== undefined && adj.sign !== null ? adj.sign : 1;
          return sum + (Number(adj.copTotal || 0) * sign);
        }, 0);
        const sumDeductions = (model.deductions || []).reduce((sum, ded) => {
          if (ded.nextPeriod) return sum;
          const sign = ded.sign !== undefined && ded.sign !== null ? ded.sign : -1;
          return sum + (Number(ded.copTotal || 0) * sign);
        }, 0);

        const grossEarnings = Math.trunc(sumEarnings);
        const adjustmentsTotal = Math.trunc(sumAdjustments);
        const deductionsTotal = Math.trunc(sumDeductions);

        model.gross_earnings = grossEarnings;
        model.adjustments_total = adjustmentsTotal;
        model.net_payment = grossEarnings + adjustmentsTotal + deductionsTotal - this.getRetentionAmount(model);
        return;
      }

      // Legacy (<=26): floor per-line, then sum (existing behavior)
      const grossEarnings = (model.earnings || []).reduce((sum, earn) => sum + Math.floor(earn.copTotal || 0), 0);
      const adjustmentsTotal = (model.adjustments || []).reduce(
        (sum, adj) => adj.nextPeriod ? sum : sum + Math.floor((adj.copTotal || 0) * adj.sign),
        0
      );
      const deductionsTotal = (model.deductions || []).reduce(
        (sum, ded) => ded.nextPeriod ? sum : sum + Math.floor((ded.copTotal || 0) * ded.sign),
        0
      );

      model.gross_earnings = grossEarnings;
      model.adjustments_total = adjustmentsTotal;
      model.net_payment = grossEarnings + adjustmentsTotal + deductionsTotal - this.getRetentionAmount(model);
    },
    hasAdjustments(model) {
      return model.adjustments && model.adjustments.length > 0 && model.adjustments.some(adj => 
        ['Bonus', 'Site Bonus', 'Studio Bonus', 'Adjustment', 'Subsidy'].includes(adj.type)
      );
    },
    hasDeductions(model) {
      return model.deductions && model.deductions.length > 0 && model.deductions.some(ded => 
        !ded.nextPeriod // Only include deductions not marked for next period
      );
    },
    getRetentionAmount(model) {
      return (model.retention_status === 'Yes' || model.retention_status === 'Always')
        ? Math.floor(model.gross_earnings * 0.04)
        : 0;
    },
    addSubsidyAdjustment(model) {
      if (!model.subsidy || model.subsidy === 'None') return;
      const subsidyGroup = this.getSubsidyGroupForModel(model);
      const subsidyDateStr = subsidyGroup === 'cb'
        ? (model.cb_payment_date || model.paymentDate)
        : model.paymentDate;
      const existingSubsidy = model.adjustments.find(adj => 
        adj.type === 'Subsidy' &&
        adj.date === subsidyDateStr &&
        this.normalizeCardGroup(adj.site_group) === subsidyGroup
      );
      if (existingSubsidy) return;

      const subsidyAmount = this.getSubsidyAmountForValue(model.subsidy);
      const paymentDate = new Date(subsidyDateStr);

      const subsidyAdjustment = {
        id: `subsidy-${model.id}-${this.selectedPeriod}`,
        model_adjustment_id: model.id,
        type: 'Subsidy',
        amount: subsidyAmount,
        currency: 'COP',
        description: 'Transportation Subsidy',
        site: subsidyGroup === 'cb' ? 'Chaturbate' : (model.default_site || 'Flirt4Free'),
        site_group: subsidyGroup,
        number: 1,
        quotas: 1,
        date: paymentDate.toISOString().split('T')[0],
        nextPeriod: 0,
        insufficientFunds: 0,
        observation: null,
        copTotal: subsidyAmount,
        sign: 1
      };

      model.adjustments.push(subsidyAdjustment);
    },
    toggleEditCommissions() {
      // If leaving edit mode, save first
      if (this.isEditingCommissions) {
        this.saveCommissions();
        this.isEditingCommissions = false;
        return;
      }

      // Entering edit mode
      if (!this.hasAnyUnlockedModels) {
        toast.error('No unlocked models available to edit');
        return;
      }

      this.isEditingCommissions = true;
    },
    updateCopTotal(model, earn) {
      // keep a consistent field for the source-currency gross
      const mapped = {
        ...earn,
        gross_earnings: earn.gross_earnings ?? earn.earnings
      };

      earn.copTotal = this.calcEarningsCopTotal(mapped);
      this.recalculateTotal(model);
    },
    getModelSalesUSD(model) {
      return model.earnings.reduce((sum, earn) => sum + (earn.earnings || 0), 0);
    },
    async saveCommissions() {
      // For non-satellite users, keep the simple rule: if their period is locked, no edits.
      // For satellite monitor, allow saving for unlocked models even if her own studio is locked.
      if (!this.isSatelliteMonitor && this.isPeriodLocked) {
        toast.error('Cannot edit commissions for a locked period');
        return;
      }

      const unlockedModels = this.filteredModels.filter(m => !m.isLocked);
      if (unlockedModels.length === 0) {
        toast.error('No unlocked models available to save');
        return;
      }

      const commissions = unlockedModels.flatMap(model =>
        (model.earnings || []).map(earn => ({
          modelId: model.id,
          platform: earn.platform,
          commission: earn.commission
        }))
      );
      const adjustmentCommissions = unlockedModels.flatMap(model =>
        (model.adjustments || [])
          .filter(adj => !adj.nextPeriod && this.canEditAdjustmentCommission(adj))
          .map(adj => ({
            modelId: model.id,
            adjustmentId: adj.id,
            commission_percent: this.getAdjustmentCommissionPercent(adj, model),
            studio_id: model.studio_id
          }))
      );

      try {
        const response = await secureAxios.post('/api/payments/update-commissions', {
          periodId: this.selectedPeriod,
          commissions,
          adjustmentCommissions
        });
        if (response.data.message === 'Commissions updated successfully') {
          toast.success('Commissions updated successfully');
          await this.fetchModels();
          this.sortOption = 'Name A → Z';
          this.sortModels('Name A → Z');
        }
      } catch (error) {
        console.error('❌ Error saving commissions:', error?.message || error);
        toast.error('Failed to update commissions');
      }
    }
  },
  mounted() {
    // Satellite monitor should land on Satellite tab
    if (this.user?.role === 'satellite_monitor') {
      this.activeTab = 'satellite';
    }
    this.fetchPeriods();
  }
};
</script>

<style scoped>
.active-card {
  border: 2px solid #1976d2 !important;
}

.payments-period-actions {
  justify-content: center;
  gap: 18px;
  margin-top: -16px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  width: 100%;
}

.payments-period-arrow {
  background: #1976d2 !important;
  color: white !important;
  height: 36px !important;
  width: 44px !important;
}

.payments-period-arrow:disabled {
  background: #d8e1ec !important;
  color: #8391a3 !important;
}

.payments-period-arrow:deep(.v-icon) {
  color: white !important;
}
</style>

<style scoped>
.v-data-table {
  overflow: visible !important;
}
</style>
